import React from 'react';
import {Box} from '@mui/material';
import {UploadMediaQuestion} from 'crqlar-react-common/dist/models/forms/form';
import EditableTypography from 'crqlar-react-common/dist/components/EditableTypography';
import MdEditor from 'crqlar-react-common/dist/components/MdEditor';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {useTranslation} from 'react-i18next';

interface UploadMediaEditorProps {
  question: UploadMediaQuestion;
  onQuestionUpdate: (question: UploadMediaQuestion) => void;
}

const UploadMediaEditor: React.FC<UploadMediaEditorProps> = ({
  question,
  onQuestionUpdate
}) => {
  const {t} = useTranslation();

  // Handle title update
  const handleTitleUpdate = (value: string) => {
    onQuestionUpdate({
      ...question,
      title: value,
    });
  };

  // Handle description update
  const handleDescriptionUpdate = (description?: string) => {
    onQuestionUpdate({
      ...question,
      description: description || ''
    });
  };

  const allowedTypes = question.allowedTypes || ['image', 'video', 'file'];
  const allowedTypesText = allowedTypes.map(type => {
    if (type === 'image') return t('Images');
    if (type === 'video') return t('Videos');
    return t('Files/Documents');
  }).join(', ');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        p: 2,
      }}
    >
      {/* Question Title */}
      <EditableTypography
        value={question.title}
        onChange={handleTitleUpdate}
        placeholder={t('Your question here')}
        variant="h6"
        multiline
        sx={{
          mb: 2,
          width: '100%',
          fontWeight: 'medium',
          textAlign: 'left',
          padding: '8px',
          borderRadius: 1,
        }}
      />

      {/* Description */}
      <Box sx={{mb: 3, width: '100%'}}>
        <MdEditor
          value={question.description || ''}
          onChange={handleDescriptionUpdate}
          placeholder={t('Question description (optional)')}
          className={'mini-mdx'}
        />
      </Box>

      {/* Upload Preview */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'grey.50',
          textAlign: 'center',
        }}
      >
        <CloudUploadIcon sx={{fontSize: 48, color: 'text.disabled', mb: 2}} />
        <Box sx={{color: 'text.secondary', fontSize: '0.875rem', mb: 0.5}}>
          {question.allowMultiple ? t('Upload multiple files') : t('Upload a file')}
        </Box>
        <Box sx={{color: 'text.disabled', fontSize: '0.75rem'}}>
          {t('Allowed types:')} {allowedTypesText}
        </Box>
      </Box>
    </Box>
  );
};

export default UploadMediaEditor;
